<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="premium-container">
        <div class="fade-in">
          <span class="premium-label">About us</span>
          <h1 class="premium-heading-lg">
            We help businesses<br>thrive online.
          </h1>
          <p class="premium-subheading">
            Digitly is a web development studio based in the UK, focused on
            building beautiful, functional digital products for small and
            medium businesses.
          </p>
        </div>
      </div>
    </section>

    <!-- Mission -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="split-layout">
          <div class="split-content fade-in">
            <span class="premium-label">Our mission</span>
            <h2 class="premium-heading">
              Making professional digital solutions accessible.
            </h2>
            <p class="premium-body" style="margin-bottom: 16px;">
              We believe every business, regardless of size or industry, deserves access to
              professional digital solutions that drive growth and efficiency.
            </p>
            <p class="premium-body">
              Our team specialises in creating custom websites and automation tools tailored
              to the unique needs of your business. We combine technical expertise with
              industry knowledge to deliver solutions that not only look great but solve
              real business challenges.
            </p>
          </div>
          <div class="split-visual fade-in fade-in-delay-2">
            <div class="visual-card">
              <div class="stat-block">
                <span class="stat-number">50+</span>
                <span class="stat-label">Projects delivered</span>
              </div>
              <div class="stat-block">
                <span class="stat-number">8</span>
                <span class="stat-label">Industries served</span>
              </div>
              <div class="stat-block">
                <span class="stat-number">24h</span>
                <span class="stat-label">Response time</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Values -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">Our values</span>
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Principles that guide everything we do.
          </h2>
        </div>

        <div class="values-grid">
          <div
            v-for="(value, index) in values"
            :key="value.title"
            class="value-item fade-in"
            :class="'fade-in-delay-' + (index + 1)"
          >
            <h3 class="value-title">{{ value.title }}</h3>
            <p class="value-desc premium-body">{{ value.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="split-layout">
          <div class="split-content fade-in">
            <span class="premium-label">Why choose us</span>
            <h2 class="premium-heading">
              Built on trust,<br>delivered with care.
            </h2>
          </div>
          <div class="reasons-list fade-in fade-in-delay-1">
            <div
              v-for="reason in reasons"
              :key="reason.title"
              class="reason-item"
            >
              <h4 class="reason-title">{{ reason.title }}</h4>
              <p class="reason-desc premium-body">{{ reason.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="cta-block text-center fade-in">
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Let's work together.
          </h2>
          <p class="premium-subheading mx-auto" style="margin-bottom: 40px;">
            Ready to take your business to the next level? We'd love to hear about your project.
          </p>
          <router-link to="/contact" class="premium-btn premium-btn-dark">
            Start your project
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useSeo, useBreadcrumbSchema } from '@/composables/useSeo'

export default {
  name: 'About',
  setup() {
    useSeo({
      title: 'About Digitly - UK Web Development Agency',
      description: 'Learn about Digitly, a professional web development agency helping UK businesses succeed online. We create custom websites and automation solutions for construction, beauty, dental, and more.',
      keywords: 'about Digitly, UK web development agency, web design company, professional developers, business automation experts',
      path: '/about'
    })

    useBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' }
    ])
  },
  data() {
    return {
      values: [
        {
          title: 'Quality first',
          description: 'We never compromise on quality. Every project is built to the highest standards with meticulous attention to detail.'
        },
        {
          title: 'Client-focused',
          description: 'Your success is our success. We prioritise your needs and goals above all else, acting as a true partner.'
        },
        {
          title: 'Transparent',
          description: 'No hidden fees, no jargon. We communicate clearly and keep you informed at every stage of the project.'
        },
        {
          title: 'Forward-thinking',
          description: 'We stay ahead of the curve, using the latest technologies and best practices to future-proof your investment.'
        }
      ],
      reasons: [
        {
          title: 'Industry expertise',
          description: 'Deep understanding of your industry\'s unique challenges and opportunities, refined across 50+ projects.'
        },
        {
          title: 'Custom solutions',
          description: 'Tailored solutions that perfectly fit your business needs and budget. No one-size-fits-all approaches.'
        },
        {
          title: 'Ongoing support',
          description: 'We\'re here for you long after launch with maintenance, updates, and dedicated support.'
        },
        {
          title: 'Proven results',
          description: 'A track record of successful projects and satisfied clients across multiple industries in the UK.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 140px 0 80px;
  background: var(--color-bg);
}

.page-header .premium-subheading {
  margin-top: 8px;
}

.split-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
}

.visual-card {
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: center;
  align-items: center;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.visual-card:hover {
  border-color: #C8C8CC;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-4px);
}

.stat-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--color-text);
  letter-spacing: -0.04em;
  line-height: 1;
}

.stat-label {
  font-size: 0.9375rem;
  color: var(--color-text-secondary);
}

.section-header {
  margin-bottom: 64px;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px 64px;
}

.value-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

.reasons-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.reason-item {
  padding-bottom: 32px;
  border-bottom: 1px solid var(--color-border);
}

.reason-item:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.reason-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

.cta-block {
  padding: 80px 0;
}

@media (max-width: 960px) {
  .page-header {
    padding: 56px 0 56px;
  }

  .split-layout {
    grid-template-columns: 1fr;
    gap: 48px;
  }

  .values-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .cta-block {
    padding: 40px 0;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 36px 0 48px;
  }
}
</style>
